import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, skip, switchMap, tap } from 'rxjs';

import { getLocationId, getPromotionName } from '../helpers/ga4-ecommerce.helper';
import { GA4EventType } from '../models/general.model';
import { GA4ProductOriginHelpers } from '../services/ga4-product-origin.helpers';

import * as GA4AnalyticsActions from './ga4-analytics.actions';

import { CoreStorageService } from '@core/infrastructure/storage/storage.service';
import { getCategoriesNavigation } from '@features/category/infrastructure/state/category.selectors';
import { LoadCategoryPageSuccess } from '@pages/category/store/category-page.actions';
import { EventFrom } from '@shared/analytics/COMMON/models/analytics-common.models';

@Injectable()
export class GA4ProductOriginEffects {
    constructor(
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly coreStorageService: CoreStorageService,
    ) { }

    /** START OF SOURCE LIST TRACKING */

    public saveListNameAndIndex$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.Ga4SendDatalayer),
            filter(({ dataLayer }) => dataLayer.event === GA4EventType.select_item),
            tap(({ dataLayer, eventFrom }) => {
                try {
                    const productOriginData = GA4ProductOriginHelpers.generateProductOriginData(
                        this.coreStorageService,
                        eventFrom,
                        dataLayer?.ecommerce?.items?.[0],
                        dataLayer?.eventData?.im_bundle_reference
                    );
                    GA4ProductOriginHelpers.setProductOriginPersisting(
                        this.coreStorageService,
                        productOriginData.productReference,
                        productOriginData.lastItemListIndex,
                        productOriginData.lastItemListName,
                        productOriginData.lastBundleReference,
                        productOriginData.pageSort,
                        productOriginData.pageGrid,
                    );
                } catch (error) {
                    console.error(error);
                }
            })
        );
    }, { dispatch: false });

    /** END OF SOURCE LIST TRACKING */

    /** START OF SOURCE PROMOTION TRACKING */

    public SetAnalyticsOriginPromotion$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.Ga4Event),
            filter((action) => action.name === GA4EventType.select_promotion),
            concatLatestFrom(() => this.store.select(getCategoriesNavigation)),
            map(([action, categories]) => {
                const promotionName = getPromotionName(categories, action?.data?.elementData);
                const locationId = getLocationId(action?.data?.elementData);

                try {
                    GA4ProductOriginHelpers.setOriginPromotion(
                        this.coreStorageService,
                        promotionName,
                        locationId
                    );
                    return GA4AnalyticsActions.SetAnalyticsOriginPromotionSuccess();
                } catch {
                    return GA4AnalyticsActions.SetAnalyticsOriginPromotionError();
                }
            }),
        );
    });

    public ClearAnalyticsOriginPromotionLoadCategory$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.SetAnalyticsOriginPromotionSuccess),
            // eslint-disable-next-line rxjs/no-unsafe-switchmap
            switchMap(() => this.actions$.pipe(ofType(LoadCategoryPageSuccess), skip(1))),
            map(() => GA4AnalyticsActions.ClearAnalyticsOriginPromotion()),
        );
    });

    public ClearAnalyticsOriginPromotionSelectItem$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.Ga4Event),
            filter((action) => action.name === GA4EventType.select_item
                && action.eventFrom !== EventFrom.parrilla),
            map(() => GA4AnalyticsActions.ClearAnalyticsOriginPromotion()),
        );
    });

    public ClearAnalyticsOriginPromotionLoadGiftcardPage$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.GiftCardPageView),
            map(() => GA4AnalyticsActions.ClearAnalyticsOriginPromotion()),
        );
    });

    public ClearAnalyticsOriginPromotion$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(GA4AnalyticsActions.ClearAnalyticsOriginPromotion),
            map(() => {
                try {
                    GA4ProductOriginHelpers.clearOriginPromotion(this.coreStorageService);

                    return GA4AnalyticsActions.ClearAnalyticsOriginPromotionSuccess();
                } catch {
                    return GA4AnalyticsActions.ClearAnalyticsOriginPromotionError();
                }
            }),
        );
    });

    /** END OF SOURCE PROMOTION TRACKING */
}
